import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import { BarChart } from "@mui/x-charts/BarChart";
import { useGetUserProgressSummeryQuery } from "../../Apis/progressApi";

export interface UserProgressSummery {
  quizName: string;
  date?: string | null;
  count: number;
}

export default function UserProgressSummeryChart() {
  const { data = [], isLoading, isError } =
    useGetUserProgressSummeryQuery();

  const reportData = useMemo<UserProgressSummery[]>(
    () => data ?? [],
    [data]
  );
  const [selectedQuiz, setSelectedQuiz] = useState("");

  // Get unique quiz names
  const quizOptions = useMemo(() => {
    return Array.from(
      new Set(reportData.map((x) => x.quizName))
    );
  }, [reportData]);

  // Set first quiz automatically
  useEffect(() => {
    if (quizOptions.length > 0 && !selectedQuiz) {
      setSelectedQuiz(quizOptions[0]);
    }
  }, [quizOptions, selectedQuiz]);
  

  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );

  if (isError)
    return (
      <Typography color="error">
        Error loading progress report.
      </Typography>
    );

  // Filter selected quiz
  const filteredData = reportData.filter(
    (x) => x.quizName === selectedQuiz
  );

  // Format chart data
  const chartData = filteredData.map((item) => ({
    label: item.date
      ? new Date(item.date).toLocaleDateString()
      : "Didn't Finish",
    value: item.count,
  }));
  const totalUsers = chartData.reduce(
    (sum, item) => sum + item.value,
    0
  );

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{ background: "#f8f8f8" }}
        title={
          <Typography
            variant="h6"
            sx={{
              color: "#512384",
              fontWeight: "bold",
            }}
          >
            User Progress Summary
          </Typography>
        }
      />

      <CardContent>
        <Box
          sx={{
            width: "100%",
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
            }}
          >
            <Typography
            variant="h6"
            sx={{
              width:1/4,
              mb: 2,
              fontWeight: "bold",
              color: "#512384",
            }}
          >
            Total Users: {totalUsers}
          </Typography>
          {/* Select Quiz */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Quiz</InputLabel>

            <Select
              value={selectedQuiz}
              label="Select Quiz"
              onChange={(e) => setSelectedQuiz(e.target.value)}
            >
              {quizOptions.map((quiz) => (
                <MenuItem key={quiz} value={quiz}>
                  {quiz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>
          

          {chartData.length > 0 ? (
            <BarChart
              dataset={chartData}
              layout="horizontal"
              height={400}
              hideLegend
              yAxis={[
                {
                  dataKey: "label",
                  width: 140,
                  colorMap: {
                    type: "ordinal",
                    values: chartData.map((x) => x.label),
                    colors: chartData.map((x) =>
                      x.label === "Didn't Finish"
                        ? "#eb3c96"
                        : "#512384"
                    ),
                  },
                },
              ]}
              xAxis={[
                {
                  label: "Users",
                },
              ]}
              series={[
                {
                  dataKey: "value",
                  label: "Users",
                  valueFormatter: (value: number | null) =>
                    `${value ?? 0} Users`,
                },
              ]}
            />
          ) : (
            <Typography>No data available.</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}